import * as THREE from 'three'
import { OBJLoader2 } from 'three/examples/jsm/loaders/OBJLoader2'
import normalizeWheel from 'normalize-wheel'
import { vertexShader, fragmentShader } from '@/assets/shaders/head'
import { lerp, MultiLerp } from '@/js/utils'
import faceModel from '@/assets/models/female_head_v4.obj'
import steps from './steps'

export default function (parentEl, start) {
  let customScrollVal = 0
  let startTime

  let box = parentEl.getBoundingClientRect()

  const objLoader = new OBJLoader2()

  const colors = { white: new THREE.Color(0xffffff), black: new THREE.Color(0x000000) }

  const scene = new THREE.Scene()
  scene.background = colors.black

  const camera = new THREE.PerspectiveCamera(95, box.width / box.height, 0.1, 1000)
  camera.position.set(...steps[0].camera.position)
  camera.lookAt(...steps[0].camera.lookAt)

  const renderer = new THREE.WebGLRenderer({ antialias: true })
  renderer.setSize(box.width, box.height)
  renderer.setPixelRatio(2)
  parentEl.appendChild(renderer.domElement)

  window.addEventListener('resize', () => {
    box = parentEl.getBoundingClientRect()
    camera.aspect = box.width / box.height
    camera.updateProjectionMatrix()
    renderer.setSize(box.width, box.height)
  })

  // eslint-disable-next-line no-unused-vars
  const zebraMaterial = new THREE.ShaderMaterial({
    vertexShader,
    fragmentShader,
    uniforms: {
      white: { type: 'vec3', value: colors.white },
      black: { type: 'vec3', value: colors.black },
      time: { type: 'float', value: 0 }
    }
  })

  let face
  objLoader.load(faceModel, obj => {
    face = obj
    face.traverse(child => {
      if (child instanceof THREE.Mesh) {
        child.material = zebraMaterial
      }
    })

    face.scale.set(...steps[0].face.scale)
    face.position.set(...steps[0].face.position)
    face.rotation.set(...steps[0].face.rotation)
    scene.add(face)

    window.addEventListener('wheel', e => {
      const scrollValues = steps.map(s => s.scrollValue)
      customScrollVal = Math.max(Math.min(...scrollValues), Math.min(customScrollVal + normalizeWheel(e).pixelY / 100, Math.max(...scrollValues)))
    })

    startTime = Date.now()
    start()
  }, ({ loaded, total }) => {
    // console.log('loading model: ' + (100 * loaded / total) + '%')
  }, err => {
    throw err
  })

  renderer.render(scene, camera)

  const splines = {
    face: {
      position: new MultiLerp(steps.map(step => step.scrollValue), steps.map(step => step.face.position)),
      rotation: new MultiLerp(steps.map(step => step.scrollValue), steps.map(step => step.face.rotation)),
      scale: new MultiLerp(steps.map(step => step.scrollValue), steps.map(step => step.face.scale))
    },
    camera: {
      position: new MultiLerp(steps.map(step => step.scrollValue), steps.map(step => step.camera.position)),
      lookAt: new MultiLerp(steps.map(step => step.scrollValue), steps.map(step => step.camera.lookAt))
    }
  }

  let progress = customScrollVal
  let oldCameraLookAt = steps[0].camera.lookAt
  function update (t) {
    zebraMaterial.uniforms.time.value = t
    progress = lerp(progress, customScrollVal, 0.1)
    camera.position.set(...triLerp(Object.values(camera.position), splines.camera.position.at(progress), 0.1))
    camera.lookAt(...triLerp(oldCameraLookAt, splines.camera.lookAt.at(progress), 0.1))
    face.position.set(...triLerp(Object.values(face.position), splines.face.position.at(progress), 0.1))
    face.rotation.set(...triLerp(Object.values(face.rotation), splines.face.rotation.at(progress), 0.1))
    face.scale.set(...triLerp(Object.values(face.scale), splines.face.scale.at(progress), 0.1))
    oldCameraLookAt = splines.camera.lookAt.at(progress)
  }

  return function () {
    const currentTime = Date.now() - startTime
    update(currentTime)
    renderer.render(scene, camera)
  }
}

function triLerp (from, to, amt) {
  return from.filter(val => typeof val === 'number').map((val, i) => lerp(from[i], to[i], amt))
}
